import React from 'react';
import { motion } from 'framer-motion';
import { Shield, FileText, Lock } from 'lucide-react';

const Legal = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
  };

  const stagger = {
    animate: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-white via-cyan-50 to-blue-50 py-24 sm:py-32">
        <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] -z-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="initial"
            animate="animate"
            variants={stagger}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div variants={fadeInUp} className="inline-flex items-center space-x-2 bg-cyan-100 text-cyan-700 px-4 py-2 rounded-full text-sm font-medium mb-8">
              <Shield size={16} />
              <span>Légal</span>
            </motion.div>
            <motion.h1
              variants={fadeInUp}
              className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6 leading-tight"
            >
              Mentions légales & Politique de confidentialité
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-gray-600 leading-relaxed"
            >
              Informations légales et protection de vos données personnelles
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
            className="space-y-12"
          >
            {/* Mentions Légales */}
            <motion.div variants={fadeInUp} className="bg-white p-8 rounded-2xl border border-gray-200">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center text-white">
                  <FileText size={20} />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">Mentions légales</h2>
              </div>

              <div className="space-y-6 text-gray-600 leading-relaxed">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Éditeur du site</h3>
                  <p>
                    <strong>Raison sociale :</strong> Kynovia Tech<br />
                    <strong>Forme juridique :</strong> SAS (Société par Actions Simplifiée)<br />
                    <strong>Capital social :</strong> 10 000 €<br />
                    <strong>Siège social :</strong> 12 Rue de la Plaine, 75000 Paris, France<br />
                    <strong>Email :</strong> contact@kynoviatech.com<br />
                    <strong>Téléphone :</strong> +33 6 68 46 69 95
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Directeur de la publication</h3>
                  <p>
                    Richard K, en qualité de représentant légal de Kynovia Tech.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Hébergeur du site</h3>
                  <p>
                    Ce site est hébergé par : KynoviaTech<br />
                    <strong>KynoviaTech</strong><br />
                    Adresse : www.kynoviatech.com<br />
                    Téléphone : +33 6 68 46 69 95
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Propriété intellectuelle</h3>
                  <p>
                    L'ensemble du contenu de ce site (textes, images, vidéos, logos) est protégé par le droit d'auteur. 
                    Toute reproduction, distribution ou utilisation sans autorisation expresse de Kynovia Tech est interdite.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Politique de Confidentialité */}
            <motion.div variants={fadeInUp} className="bg-white p-8 rounded-2xl border border-gray-200">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center text-white">
                  <Lock size={20} />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">Politique de confidentialité</h2>
              </div>

              <div className="space-y-6 text-gray-600 leading-relaxed">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Collecte des données personnelles</h3>
                  <p>
                    Kynovia Tech collecte vos données personnelles uniquement dans le cadre de :
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1 ml-4">
                    <li>La gestion des demandes de devis via le formulaire de contact</li>
                    <li>La gestion de la relation client</li>
                    <li>L'envoi d'informations commerciales (avec votre consentement)</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Données collectées</h3>
                  <p>
                    Les données collectées peuvent inclure :
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1 ml-4">
                    <li>Nom et prénom</li>
                    <li>Adresse email</li>
                    <li>Numéro de téléphone</li>
                    <li>Nom de l'entreprise</li>
                    <li>Informations relatives à votre projet</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Utilisation des données</h3>
                  <p>
                    Vos données personnelles sont utilisées pour :
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1 ml-4">
                    <li>Répondre à vos demandes de devis et d'information</li>
                    <li>Assurer le suivi de nos relations commerciales</li>
                    <li>Améliorer nos services</li>
                    <li>Vous envoyer des communications marketing (avec votre consentement)</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Durée de conservation</h3>
                  <p>
                    Vos données personnelles sont conservées pendant la durée nécessaire aux finalités pour lesquelles elles sont collectées, 
                    conformément aux obligations légales applicables.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Vos droits (RGPD)</h3>
                  <p>
                    Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez des droits suivants :
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1 ml-4">
                    <li><strong>Droit d'accès :</strong> Vous pouvez accéder à vos données personnelles</li>
                    <li><strong>Droit de rectification :</strong> Vous pouvez demander la correction de données inexactes</li>
                    <li><strong>Droit à l'effacement :</strong> Vous pouvez demander la suppression de vos données</li>
                    <li><strong>Droit d'opposition :</strong> Vous pouvez vous opposer au traitement de vos données</li>
                    <li><strong>Droit à la portabilité :</strong> Vous pouvez demander le transfert de vos données</li>
                    <li><strong>Droit de limitation :</strong> Vous pouvez demander la limitation du traitement</li>
                  </ul>
                  <p className="mt-4">
                    Pour exercer vos droits, contactez-nous à : <a href="mailto:contact@kynoviatech.com" className="text-cyan-600 hover:underline">contact@kynoviatech.com</a>
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Sécurité des données</h3>
                  <p>
                    Kynovia Tech met en œuvre toutes les mesures techniques et organisationnelles nécessaires pour assurer la sécurité 
                    et la confidentialité de vos données personnelles.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Cookies</h3>
                  <p>
                    Ce site peut utiliser des cookies pour améliorer votre expérience de navigation. Vous pouvez paramétrer votre navigateur 
                    pour refuser les cookies, mais cela peut affecter certaines fonctionnalités du site.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Modifications de la politique</h3>
                  <p>
                    Kynovia Tech se réserve le droit de modifier cette politique de confidentialité à tout moment. 
                    Les modifications entreront en vigueur dès leur publication sur cette page.
                  </p>
                </div>

                <div className="bg-cyan-50 p-6 rounded-xl border border-cyan-200 mt-8">
                  <p className="text-sm">
                    <strong>Dernière mise à jour :</strong> Octobre 2025
                  </p>
                  <p className="text-sm mt-2">
                    Pour toute question concernant cette politique de confidentialité, contactez-nous à :<br />
                    <a href="mailto:contact@kynoviatech.com" className="text-cyan-600 hover:underline font-medium">contact@kynoviatech.com</a>
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Legal;